import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const SCROLL_END = '+=600%';
const STEPS = 6; // intro, sydney, melbourne, brisbane, perth, final

const propertyCards = [
  {
    id: 'sydney',
    city: 'Sydney',
    image: 'https://images.pexels.com/photos/1035725/pexels-photo-1035725.jpeg?auto=compress&cs=tinysrgb&w=800',
    className: 'top-[8%] left-[3%] md:left-[5%]',
  },
  {
    id: 'melbourne',
    city: 'Melbourne',
    image: 'https://images.pexels.com/photos/33263106/pexels-photo-33263106.jpeg?auto=compress&cs=tinysrgb&w=800',
    className: 'top-[5%] right-[3%] md:right-[5%]',
  },
  {
    id: 'brisbane',
    city: 'Brisbane',
    image: 'https://images.pexels.com/photos/2590144/pexels-photo-2590144.jpeg?auto=compress&cs=tinysrgb&w=800',
    className: 'bottom-[15%] left-[3%] md:left-[8%]',
  },
  {
    id: 'perth',
    city: 'Perth',
    image: 'https://images.pexels.com/photos/35355651/pexels-photo-35355651.jpeg?auto=compress&cs=tinysrgb&w=800',
    className: 'bottom-[12%] right-[3%] md:right-[8%]',
  },
  {
    id: 'adelaide',
    city: 'Adelaide',
    image: 'https://images.pexels.com/photos/2096700/pexels-photo-2096700.jpeg?auto=compress&cs=tinysrgb&w=800',
    className: 'top-[42%] left-[0%] md:left-[1%]',
  },
  {
    id: 'canberra',
    city: 'Canberra',
    image: 'https://images.pexels.com/photos/12532604/pexels-photo-12532604.jpeg?auto=compress&cs=tinysrgb&w=800',
    className: 'top-[38%] right-[0%] md:right-[1%]',
  },
];

const cities = ['sydney', 'melbourne', 'brisbane', 'perth'];

const ScrollShowcase = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ── Scroll-driven zoom on background image ──────────────────
      gsap.fromTo('[data-bg-image]',
        { scale: 1.0 },
        {
          scale: 1.25,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top top',
            end: SCROLL_END,
            scrub: 2,
          },
        }
      );

      // Set initial hidden states for animated elements
      gsap.set('[data-card]', { opacity: 0, scale: 0.65, y: 80 });
      gsap.set('[data-city-text]', { opacity: 0, filter: 'blur(20px)', y: 40 });
      gsap.set('[data-final-text]', { opacity: 0, filter: 'blur(20px)', y: 30 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: SCROLL_END,
          pin: true,
          scrub: 1.5,
        },
      });

      // Step 0 – Hold intro text visible
      tl.to({}, { duration: 1.5 });

      // Fade out intro
      tl.to('[data-intro]', {
        opacity: 0,
        y: -60,
        filter: 'blur(15px)',
        duration: 1,
      });

      // Step 1 – Sydney
      tl.to('[data-card="sydney"]', { opacity: 1, scale: 1, y: 0, duration: 1.2 }, '<0.3')
        .to('[data-city-text="sydney"]', { opacity: 1, filter: 'blur(0px)', y: 0, duration: 0.8 }, '<0.5')
        .to({}, { duration: 2 })
        .to('[data-city-text="sydney"]', { opacity: 0, filter: 'blur(10px)', y: -30, duration: 0.6 });

      // Step 2 – Melbourne
      tl.to('[data-card="melbourne"]', { opacity: 1, scale: 1, y: 0, duration: 1.2 }, '<0.2')
        .to('[data-city-text="melbourne"]', { opacity: 1, filter: 'blur(0px)', y: 0, duration: 0.8 }, '<0.5')
        .to({}, { duration: 2 })
        .to('[data-city-text="melbourne"]', { opacity: 0, filter: 'blur(10px)', y: -30, duration: 0.6 });

      // Step 3 – Brisbane
      tl.to('[data-card="brisbane"]', { opacity: 1, scale: 1, y: 0, duration: 1.2 }, '<0.2')
        .to('[data-city-text="brisbane"]', { opacity: 1, filter: 'blur(0px)', y: 0, duration: 0.8 }, '<0.5')
        .to({}, { duration: 2 })
        .to('[data-city-text="brisbane"]', { opacity: 0, filter: 'blur(10px)', y: -30, duration: 0.6 });

      // Step 4 – Perth
      tl.to('[data-card="perth"]', { opacity: 1, scale: 1, y: 0, duration: 1.2 }, '<0.2')
        .to('[data-city-text="perth"]', { opacity: 1, filter: 'blur(0px)', y: 0, duration: 0.8 }, '<0.5')
        .to({}, { duration: 2 })
        .to('[data-city-text="perth"]', { opacity: 0, filter: 'blur(10px)', y: -30, duration: 0.6 });

      // Step 5 – Final RentalX
      tl.to('[data-final-text]', { opacity: 1, filter: 'blur(0px)', y: 0, duration: 1.2 }, '<0.3')
        .to({}, { duration: 3 });

      // Progress dots – pure DOM update, no React state
      const dots = sectionRef.current?.querySelectorAll('[data-dot]');
      if (dots && dots.length) {
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top top',
          end: SCROLL_END,
          onUpdate: (self) => {
            const activeStep = Math.min(Math.floor(self.progress * STEPS), STEPS - 1);
            dots.forEach((dot, i) => {
              dot.style.transform = i === activeStep ? 'scale(1.5)' : 'scale(1)';
              dot.style.opacity = i === activeStep ? '1' : i < activeStep ? '0.5' : '0.25';
            });
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative h-screen overflow-hidden"
      data-testid="scroll-showcase"
    >
      {/* Blurred students background */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/7972533/pexels-photo-7972533.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt="Students campus"
          data-bg-image=""
          className="w-full h-full object-cover"
          style={{ filter: 'blur(4px)', willChange: 'transform' }}
        />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Intro text – starts fully visible */}
      <div
        data-intro=""
        className="absolute inset-0 flex items-center justify-center z-20 px-6 md:px-12"
        data-testid="hero-intro-text"
      >
        <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center max-w-4xl leading-tight drop-shadow-2xl">
          Every year, thousands of students struggle to find the right accommodation
        </p>
      </div>

      {/* City name texts – all start hidden */}
      {cities.map((city) => (
        <div
          key={city}
          data-city-text={city}
          className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
        >
          <h2 className="text-7xl sm:text-8xl md:text-9xl font-black text-white tracking-tight capitalize drop-shadow-2xl">
            {city.charAt(0).toUpperCase() + city.slice(1)}
          </h2>
        </div>
      ))}

      {/* Final RentalX text – starts hidden */}
      <div
        data-final-text=""
        className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
        data-testid="hero-final-text"
      >
        <div className="text-center px-6">
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/80 mb-3 font-medium tracking-wide drop-shadow-lg">
            Discover your perfect stay with
          </p>
          <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black text-white tracking-tight drop-shadow-2xl flex items-center justify-center leading-none">
            {/* "Rental" stays pure white */}
            <span>Rental</span>
            {/* Single space + X from SVG — displayed directly, no cropping or scale tricks */}
            <img
              src="/rentalx-x-new.svg"
              alt="X"
              style={{
                height: '0.82em',
                width: 'auto',
                display: 'inline-block',
                verticalAlign: 'middle',
                marginLeft: '0.08em',
                marginBottom: '0.04em',
                flexShrink: 0,
              }}
            />
          </h1>
        </div>
      </div>

      {/* Floating City Cards – bigger sizes */}
      {propertyCards.map((card) => (
        <div
          key={card.id}
          data-card={card.id}
          className={`absolute ${card.className} w-40 h-52 sm:w-48 sm:h-60 md:w-56 md:h-72 lg:w-64 lg:h-80 rounded-xl overflow-hidden shadow-2xl z-10`}
        >
          <img
            src={card.image}
            alt={card.city}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4 text-white">
            <p className="text-sm md:text-lg font-bold drop-shadow-lg">{card.city}</p>
            <p className="text-xs opacity-80">Australia</p>
          </div>
        </div>
      ))}

      {/* Progress dots */}
      <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-2 md:gap-3">
        {Array.from({ length: STEPS }).map((_, i) => (
          <div
            key={i}
            data-dot={i}
            className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full bg-white transition-transform duration-200"
            style={{ opacity: i === 0 ? 1 : 0.25 }}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center text-white/60">
        <span className="text-xs mb-2 font-medium tracking-widest uppercase">Scroll to explore</span>
        <div className="w-5 h-8 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-2 bg-white rounded-full mt-1.5 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default ScrollShowcase;
